import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile anti-bot widget. Renders only when VITE_TURNSTILE_SITE_KEY
 * is set — otherwise it's a no-op, so login/apply forms work unchanged until the
 * key is configured (matching the backend, which skips verification without a
 * secret). The solved token is handed to `onVerify` and posted as `captchaToken`.
 *
 * Turnstile tokens are SINGLE-USE. After a rejected submit the parent must call
 * `ref.reset()` so the retry mints a fresh token instead of re-sending the spent
 * one — Cloudflare rejects a reused token as `timeout-or-duplicate`, which would
 * surface as a bogus "Captcha verification failed" on the second attempt.
 */
declare global {
  interface Window { turnstile?: any }
}

export interface TurnstileHandle {
  /** Discard the current token and re-issue a fresh one. Call after a failed submit. */
  reset: () => void;
}

const SITE_KEY = (import.meta.env as Record<string, string | undefined>).VITE_TURNSTILE_SITE_KEY;
export const captchaEnabled = !!SITE_KEY;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

const Turnstile = forwardRef<TurnstileHandle, { onVerify: (token: string) => void }>(
  function Turnstile({ onVerify }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const cb = useRef(onVerify);
    cb.current = onVerify;
    const widgetId = useRef<string | null>(null);
    const [failed, setFailed] = useState(false);

    // Imperative reset so a parent can recycle the single-use token after a
    // rejected submit. Clears the spent token first (so the submit button
    // disables until a fresh one arrives), then re-runs the widget — which
    // fires `callback` again with a new token for non-interactive modes.
    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetId.current != null && window.turnstile) {
          cb.current('');
          setFailed(false);
          try { window.turnstile.reset(widgetId.current); } catch { /* ignore */ }
        }
      },
    }), []);

    useEffect(() => {
      if (!SITE_KEY || !containerRef.current) return;
      let cancelled = false;
      loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;
          widgetId.current = window.turnstile.render(containerRef.current, {
            sitekey: SITE_KEY,
            callback: (token: string) => { setFailed(false); cb.current(token); },
            /**
             * A Turnstile token dies after about 300 seconds, and the widget solves on
             * page load — so the clock starts before the applicant has typed anything.
             *
             * This used to clear the token and stop there. The parent form disables its
             * submit button while `captchaToken` is empty, so five minutes after landing
             * the Apply button went dead, permanently, with nothing on screen to explain
             * it. An applicant who read the job description properly or went to find
             * their phone number was silently locked out of applying, and the ones who
             * did get a submit away sent a stale token — which is the
             * `timeout-or-duplicate` rejection visible in the production logs.
             *
             * refresh-expired: auto makes Turnstile mint a replacement itself, which
             * fires `callback` above and re-enables the button. The handler still clears
             * the spent token so the form cannot post an expired one in the gap.
             */
            'expired-callback': () => cb.current(''),
            'refresh-expired': 'auto',
            // Fires on a misconfigured domain (error 110200), a blocked script, or a
            // Cloudflare incident. Without a visible message the user just sees a
            // permanently disabled button and no reason — surface it instead.
            'error-callback': () => { setFailed(true); cb.current(''); },
          });
        })
        .catch(() => setFailed(true));
      return () => {
        cancelled = true;
        if (widgetId.current != null && window.turnstile) {
          try { window.turnstile.remove(widgetId.current); } catch { /* ignore */ }
          widgetId.current = null;
        }
      };
    }, []);

    if (!SITE_KEY) return null;
    return (
      <div className="my-1">
        <div ref={containerRef} />
        {failed && (
          <p className="mt-1.5 text-xs text-red-600" role="alert">
            The security check couldn’t load. Please refresh the page or try a different
            network/browser. If this keeps happening, contact your administrator.
          </p>
        )}
      </div>
    );
  },
);

export default Turnstile;
